import React, { useState, useRef, useEffect, useCallback } from 'react';

import { SoundInIcon } from '@/assets/icons/v2/SoundIn';
import { NoSoundIcon } from '@/assets/icons/v2/NoSound';
import { PERCENTAGE_MULTIPLIER, ICON_SIZE } from '../../utils/constants';

import * as S from '../../styles';

interface VolumeProps {
	volume: number;
	onToggleSound: () => void;
	onVolumeChange: (volume: number) => void;
}

function renderVolumeIcon(isMuted: boolean): JSX.Element {
	return isMuted ? (
		<NoSoundIcon width={ICON_SIZE} height={ICON_SIZE} />
	) : (
		<SoundInIcon width={ICON_SIZE} height={ICON_SIZE} />
	);
}

export function Volume({ volume, onToggleSound, onVolumeChange }: Readonly<VolumeProps>): JSX.Element {
	const [showSlider, setShowSlider] = useState<boolean>(false);
	const hideTimeoutRef = useRef<number | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const volumePercent = volume * PERCENTAGE_MULTIPLIER;
	const isMuted = volume === 0;

	const getPopupContainer = useCallback(() => wrapperRef.current || document.body, []);

	function handleSliderChange(value: number | number[]): void {
		const rawValue = Array.isArray(value) ? value[0] : value;
		const newVolume = rawValue / PERCENTAGE_MULTIPLIER;
		if (!Number.isFinite(newVolume)) return;
		onVolumeChange(newVolume);
	}

	function handleMouseEnter(): void {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
		setShowSlider(true);
	}

	function handleMouseLeave(): void {
		hideTimeoutRef.current = window.setTimeout(() => {
			setShowSlider(false);
		}, 200);
	}

	useEffect(() => {
		return () => {
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		};
	}, []);

	return (
		<S.ControlItem onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
			<S.ControlButton icon={<S.HoverIcon>{renderVolumeIcon(isMuted)}</S.HoverIcon>} onClick={onToggleSound} />
			{showSlider && (
				<S.VolumeSliderWrapper ref={wrapperRef}>
					<S.VolumeSlider
						value={volumePercent}
						onChange={handleSliderChange}
						min={0}
						max={PERCENTAGE_MULTIPLIER}
						vertical
						getTooltipPopupContainer={getPopupContainer}
					/>
				</S.VolumeSliderWrapper>
			)}
		</S.ControlItem>
	);
}
